import { useCallback, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';
import type { ExportPetitionContentType, ExportPetitionFormat } from '@change-corgi/middle-end-gql-schema';

import { useLocale } from 'src/app/shared/hooks/l10n';
import type { MutationState } from 'src/app/shared/utils/async';

import type { ExportErrorCode } from '../../constants';
import { EXPORT_ERROR_MAP, exportErrorCodes } from '../../constants';

import { exportPetition } from './api';

type RequestState = MutationState<{ data: { jobId?: string } }, { error: ExportErrorCode }>;

type UsePetitionExportParams = {
	id: string;
	type: ExportPetitionContentType;
	format: ExportPetitionFormat;
	createdAt: string;
};
type UsePetitionExportOptions = {
	onSuccess?: (data: { jobId?: string }) => void;
	onError?: (error: ExportErrorCode) => void;
};

type UsePetitionExportReturn = RequestState & {
	mutate: (
		params: UsePetitionExportParams,
	) => Promise<Extract<RequestState, { status: 'loaded' } | { status: 'error' }>>;
	reset: () => void;
};

export function usePetitionExport({ onSuccess, onError }: UsePetitionExportOptions = {}): UsePetitionExportReturn {
	const utilityContext = useUtilityContext();
	const locale = useLocale();
	const [state, setState] = useState<RequestState>({ status: 'idle' });

	const handleExport = useCallback(
		async ({ id, type, format, createdAt }: UsePetitionExportParams) => {
			setState({ status: 'loading' });

			try {
				const result = await exportPetition({ id, type, format, locale, createdAt }, utilityContext);
				const successStatus = { status: 'loaded' as const, data: result };
				setState(successStatus);
				onSuccess?.(result);
				return successStatus;
			} catch (e) {
				const errorMessage = parseError(e);
				const errorStatus = { status: 'error' as const, error: errorMessage };
				setState(errorStatus);
				onError?.(errorMessage);
				return errorStatus;
			}
		},
		[locale, onError, onSuccess, utilityContext],
	);

	const handleReset = useCallback(() => {
		setState({ status: 'idle' });
	}, []);

	return { mutate: handleExport, reset: handleReset, ...state };
}

function parseError(error: unknown) {
	const err = error instanceof Error ? error.message : undefined;
	return exportErrorCodes.find((val) => val === err) ?? EXPORT_ERROR_MAP.GENERIC_ERROR.code;
}
